/**
 * Derive the SMS-funnel + unique-based ratios from a raw stat row.
 * For Media Devoted | Created By Ciro Autiero
 *
 * Companion to {@link deriveFinancialMetrics}: same pure, idempotent, additive
 * contract (`Object.assign` the returned partial; a key already present with a
 * finite value is never overwritten), for the nine fields the Flow Optimizer
 * TAG RULES offer as conditions but neither provider reliably supplies —
 * ImCTR, uCTR, Click2Reg, Reg2Dep, uEPV, CPuC, CPL, CPS and EPL.
 *
 * Why this must exist: Voluum's optimizer report only requests the additive
 * base columns, and the per-item aggregator copies only additive keys (a ratio
 * cannot be summed across rows). So every one of these fields was absent from
 * the optimizer cube and `flowTagMetricValue` fell back to 0 — which silently
 * turned `Reg2Dep <= 3` into "always true" and `Reg2Dep >= 3` into "never
 * true". Deriving them from this entity's own aggregated counters keeps the
 * tag verdict, the score explainer and the hover card on the same numbers.
 *
 * Formulas match reports.totals.util (`sumMetricsTotals`) and
 * list-performance.metrics (`toFullMetrics`) so a metric means the same thing
 * on the FO preview as it does on the SMSWiser lists/drops tabs.
 */

import type { FinancialMetrics } from "./derive-financial-metrics";

const FINITE = (v: unknown): v is number =>
  typeof v === "number" && Number.isFinite(v);

function read(src: Readonly<FinancialMetrics>, ...keys: string[]): number {
  for (const key of keys) {
    if (FINITE(src[key])) return src[key];
  }
  return 0;
}

function has(src: Readonly<FinancialMetrics>, key: string): boolean {
  return FINITE(src[key]);
}

function pct(numerator: number, denominator: number): number {
  return denominator > 0 ? (numerator / denominator) * 100 : 0;
}

function ratio(numerator: number, denominator: number): number {
  return denominator > 0 ? numerator / denominator : 0;
}

export function deriveFunnelMetrics(
  src: Readonly<FinancialMetrics>,
): Partial<FinancialMetrics> {
  const out: FinancialMetrics = {};
  // `sends` is the SMSWiser name for impressions; the id aliases mirror
  // extractVoluumMetrics / normalizeBinomMetricsForDashboard.
  const impressions = read(src, "impressions", "sends");
  const uniqueVisits = read(src, "uniqueVisits", "visits");
  const uniqueClicks = read(src, "uniqueClicks", "clicks");
  const registrations = read(src, "customConversions6", "registrations");
  const ftd = read(src, "customConversions7", "ftd");
  const conversions = read(src, "conversions", "leads");
  const revenue = read(src, "revenue");
  const cost = read(src, "cost");

  if (!has(src, "ImCTR")) out.ImCTR = pct(uniqueClicks, impressions);
  if (!has(src, "uCTR")) out.uCTR = pct(uniqueClicks, uniqueVisits);
  if (!has(src, "Click2Reg")) out.Click2Reg = pct(registrations, uniqueClicks);
  if (!has(src, "Reg2Dep")) out.Reg2Dep = pct(ftd, registrations);
  if (!has(src, "uEPV")) out.uEPV = ratio(revenue, uniqueVisits);
  if (!has(src, "CPuC")) out.CPuC = ratio(cost, uniqueClicks);
  if (!has(src, "CPL")) out.CPL = ratio(cost, registrations);
  if (!has(src, "CPS")) out.CPS = ratio(cost, impressions);
  // Earnings per lead = revenue / CONVERSIONS — the trackers' own EPL basis
  // (verified against the Voluum panel column; Binom's "leads" maps to
  // conversions). NOT customConversions6/REG, which CPL divides by. Binom
  // sends EPL:: columns natively but folded ratios can't be summed, so the
  // normalizer deletes them and this re-derivation IS the tracker's number.
  if (!has(src, "epl")) out.epl = ratio(revenue, conversions);
  return out;
}
